import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const empty = {
  name: "",
  email: "",
  phone: "",
  equipment_id: "",
  start_date: "",
  end_date: "",
  delivery_address: "",
  notes: "",
};

const today = () => format(new Date(), "yyyy-MM-dd");

export default function BookingDialog({
  open,
  onOpenChange,
  equipmentList,
  initialEquipment,
  apiUrl,
}) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        ...empty,
        equipment_id: initialEquipment?.id || "",
      });
      setErrors({});
    }
  }, [open, initialEquipment]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Valid email required";
    if (!form.phone.trim() || form.phone.trim().length < 6) e.phone = "Phone required";
    if (!form.equipment_id) e.equipment_id = "Select equipment";
    if (!form.start_date) e.start_date = "Pick a start date";
    if (!form.end_date) e.end_date = "Pick an end date";
    if (form.start_date && form.end_date && form.end_date < form.start_date)
      e.end_date = "End must be on/after start";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${apiUrl}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        equipment_id: form.equipment_id,
        start_date: form.start_date,
        end_date: form.end_date,
        delivery_address: form.delivery_address.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success("Inquiry sent", {
        description: "We'll get back to you shortly with a quote.",
      });
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Couldn't send inquiry", {
        description:
          typeof detail === "string"
            ? detail
            : "Please check the form and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl tracking-tight">
            Request a Quote
          </DialogTitle>
          <DialogDescription>
            Tell us about your job — we'll come back with availability and a price.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 mt-2" data-testid="booking-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" error={errors.name}>
              <Input
                data-testid="form-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="John Smith"
                className="rounded-sm"
              />
            </Field>
            <Field label="Phone" error={errors.phone}>
              <Input
                data-testid="form-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="04xx xxx xxx"
                className="rounded-sm"
              />
            </Field>
          </div>

          <Field label="Email" error={errors.email}>
            <Input
              data-testid="form-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@email.com"
              className="rounded-sm"
            />
          </Field>

          <Field label="Equipment" error={errors.equipment_id}>
            <Select
              value={form.equipment_id}
              onValueChange={(v) => update("equipment_id", v)}
            >
              <SelectTrigger data-testid="form-equipment" className="rounded-sm">
                <SelectValue placeholder="Choose equipment" />
              </SelectTrigger>
              <SelectContent>
                {equipmentList.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`form-equipment-option-${e.id}`}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Start date" error={errors.start_date}>
              <Input
                data-testid="form-start-date"
                type="date"
                value={form.start_date}
                min={today()}
                onChange={(e) => update("start_date", e.target.value)}
                className="rounded-sm"
              />
            </Field>
            <Field label="End date" error={errors.end_date}>
              <Input
                data-testid="form-end-date"
                type="date"
                value={form.end_date}
                min={form.start_date || today()}
                onChange={(e) => update("end_date", e.target.value)}
                className="rounded-sm"
              />
            </Field>
          </div>

          <Field label="Delivery address (optional)">
            <Input
              data-testid="form-address"
              value={form.delivery_address}
              onChange={(e) => update("delivery_address", e.target.value)}
              placeholder="Street, suburb, postcode"
              className="rounded-sm"
            />
          </Field>

          <Field label="Job notes (optional)">
            <Textarea
              data-testid="form-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="What you're working on, access, attachments needed..."
              rows={3}
              className="rounded-sm"
            />
          </Field>

          <DialogFooter className="mt-2 flex sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="btn-sharp font-heading uppercase tracking-wider"
              data-testid="form-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="form-submit-btn"
              className="btn-sharp bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wider"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                </>
              ) : (
                "Send Inquiry"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, error }) {
  return (
    <div className="grid gap-1.5">
      <Label className="font-heading uppercase tracking-wider text-xs text-secondary">
        {label}
      </Label>
      {children}
      {error && (
        <span className="text-xs text-red-600" data-testid="form-field-error">
          {error}
        </span>
      )}
    </div>
  );
}
